/**
 * Date Role - Validate Pillar
 *
 * Validação de valores de data.
 * ZERO dependências de outros pilares - totalmente standalone.
 *
 * @example
 * import { validateDate, isValidDate } from '@attrx/role-morphic/date/validate';
 *
 * validateDate('iso', '2024-12-05T19:30:00.000Z');  // { valid: true, errors: [] }
 * validateDate('timestamp', 1733425800000);         // { valid: true, errors: [] }
 * validateDate('epoch', 1733425800);                // { valid: true, errors: [] }
 */

import type { DateVariant } from "./types";

// =============================================================================
// TYPES
// =============================================================================

export type ValidationResult = { valid: boolean; errors: string[] };

// =============================================================================
// REGEX PATTERNS
// =============================================================================

const ISO_DATE_TIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
const ISO_DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

// =============================================================================
// VALIDATE BY VARIANT
// =============================================================================

/**
 * Valida valor ISO 8601
 */
export function validateIso(value: unknown): ValidationResult {
  const errors: string[] = [];

  if (typeof value !== "string") {
    errors.push("ISO date must be a string");
    return { valid: false, errors };
  }

  const trimmed = value.trim();

  // Verifica se é um formato ISO válido
  if (!ISO_DATE_TIME_REGEX.test(trimmed) && !ISO_DATE_ONLY_REGEX.test(trimmed)) {
    errors.push("Invalid ISO 8601 format");
    return { valid: false, errors };
  }

  // Verifica se a data é parseável
  const date = new Date(trimmed);
  if (isNaN(date.getTime())) {
    errors.push("Invalid date value");
    return { valid: false, errors };
  }

  return { valid: true, errors: [] };
}

/**
 * Valida valor timestamp (milissegundos)
 */
export function validateTimestamp(value: unknown): ValidationResult {
  const errors: string[] = [];

  if (typeof value !== "number") {
    errors.push("Timestamp must be a number");
    return { valid: false, errors };
  }

  if (!Number.isFinite(value)) {
    errors.push("Timestamp must be a finite number");
    return { valid: false, errors };
  }

  if (!Number.isInteger(value)) {
    errors.push("Timestamp must be an integer");
    return { valid: false, errors };
  }

  return { valid: true, errors: [] };
}

/**
 * Valida valor epoch (segundos)
 */
export function validateEpoch(value: unknown): ValidationResult {
  const errors: string[] = [];

  if (typeof value !== "number") {
    errors.push("Epoch must be a number");
    return { valid: false, errors };
  }

  if (!Number.isFinite(value)) {
    errors.push("Epoch must be a finite number");
    return { valid: false, errors };
  }

  if (!Number.isInteger(value)) {
    errors.push("Epoch must be an integer");
    return { valid: false, errors };
  }

  return { valid: true, errors: [] };
}

// =============================================================================
// MAIN VALIDATE
// =============================================================================

/**
 * Valida valor de data para uma variante específica
 *
 * @param variant - Variante a validar ("iso", "timestamp", "epoch")
 * @param value - Valor a validar
 * @returns Resultado da validação
 *
 * @example
 * validateDate('iso', '2024-12-05T19:30:00.000Z');
 * // { valid: true, errors: [] }
 *
 * validateDate('iso', 'invalid');
 * // { valid: false, errors: ['Invalid ISO 8601 format'] }
 */
export function validateDate(variant: DateVariant, value: unknown): ValidationResult {
  switch (variant) {
    case "iso":
      return validateIso(value);
    case "timestamp":
      return validateTimestamp(value);
    case "epoch":
      return validateEpoch(value);
    default:
      return { valid: false, errors: [`Unknown date variant: ${variant}`] };
  }
}

/**
 * Verifica se valor é válido (retorna boolean)
 *
 * @example
 * isValidDate('iso', '2024-12-05T19:30:00.000Z');  // true
 * isValidDate('iso', 'invalid');                   // false
 */
export function isValidDate(variant: DateVariant, value: unknown): boolean {
  return validateDate(variant, value).valid;
}

/**
 * Verifica se variante existe
 */
export function hasDateVariant(variant: string): variant is DateVariant {
  return variant === "iso" || variant === "timestamp" || variant === "epoch";
}
